import * as Sentry from '@sentry/react';
import WindowResource from '../resources/WindowResource';
import { getStorefrontOrigin } from '../../modules/storefront/exports/Cookies';

type Primitive = string | number | boolean | null | undefined;
export type DiagnosticAttrs = Record<string, Primitive>;

const STOREFRONT_BASE_PATH_COOKIE = 'storefront_base_path';

let pageUnloading = false;

if (typeof window !== 'undefined') {
  const markUnloading = () => {
    pageUnloading = true;
  };

  const markStaying = () => {
    pageUnloading = false;
  };

  window.addEventListener('beforeunload', markUnloading);
  window.addEventListener('pagehide', markUnloading);
  window.addEventListener('pageshow', markStaying);
  // A cancelled unload (the leave-page prompt, an intercepted navigation) never fires pageshow; the
  // page coming back to the foreground is the sign it stayed.
  window.addEventListener('focus', markStaying);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      markStaying();
    }
  });
}

function isPageLeaving(): boolean {
  return pageUnloading || document.visibilityState === 'hidden';
}

function readCookieEntries(): string[] {
  try {
    return document.cookie ? document.cookie.split('; ') : [];
  } catch {
    return [];
  }
}

const REPORTED_STORAGE_KEY = 'page_diagnostics_reported';

const reportedThisPage = new Set<string>();

/**
 * True the first time `key` is seen in this tab's session. Kept in sessionStorage as well, so a page
 * that reloads itself does not report the same thing on every load.
 */
function markReportedOnce(key: string): boolean {
  if (reportedThisPage.has(key)) {
    return false;
  }

  reportedThisPage.add(key);

  try {
    const reported: string[] = JSON.parse(window.sessionStorage.getItem(REPORTED_STORAGE_KEY) ?? '[]');

    if (reported.includes(key)) {
      return false;
    }

    window.sessionStorage.setItem(REPORTED_STORAGE_KEY, JSON.stringify([...reported, key]));
  } catch {
    // Storage blocked or full: the in-memory set still dedupes within this page.
  }

  return true;
}

function referrerWithoutQuery(): string | null {
  try {
    if (!document.referrer) {
      return null;
    }

    const url = new URL(document.referrer);

    return url.origin + url.pathname;
  } catch {
    return null;
  }
}

/**
 * Everything that decides how the SPA resolved its base path and slug, without values that could
 * identify the customer: cookie NAMES only, except the base-path cookie, whose every copy is listed
 * because `getCookie` silently takes the last one.
 */
function storefrontSnapshot(): DiagnosticAttrs {
  const entries = readCookieEntries();
  const basePathCopies = entries
    .filter((entry) => entry.startsWith(`${STOREFRONT_BASE_PATH_COOKIE}=`))
    .map((entry) => {
      const raw = entry.slice(STOREFRONT_BASE_PATH_COOKIE.length + 1);

      try {
        return decodeURIComponent(raw);
      } catch {
        return raw;
      }
    });

  return {
    is_storefront: !!getStorefrontOrigin(),
    resolved_base_path: WindowResource.basePath || null,
    base_path_env: import.meta.env.VITE_CONFIG_BASE_PATH ?? null,
    base_path_cookie_copies: JSON.stringify(basePathCopies),
    storefront_cookie_names: entries
      .map((entry) => entry.split('=')[0])
      .filter((name) => name.startsWith('storefront_'))
      .join(','),
  };
}

function pageSnapshot(): DiagnosticAttrs {
  const navigation = performance.getEntriesByType?.('navigation')?.[0] as PerformanceNavigationTiming | undefined;

  return {
    path: window.location.pathname,
    referrer: referrerWithoutQuery(),
    navigation_type: navigation?.type,
    history_length: window.history.length,
    visibility: document.visibilityState,
    online: navigator.onLine,
    cookies_enabled: navigator.cookieEnabled,
    page_unloading: pageUnloading,
  };
}

const pageDiagnostics = {
  isPageUnloading(): boolean {
    return pageUnloading;
  },

  /**
   * Firefox ends an XHR that a navigation cuts off with a bare `loadend` (status 0, empty body), and
   * axios resolves that as a success. Recorded as a breadcrumb so the next error shows which request
   * was cut off and whether the page was leaving at the time.
   */
  responseLessRequest(method: string | undefined, url: string | undefined): void {
    Sentry.addBreadcrumb({
      category: 'api.response_less',
      level: 'warning',
      message: `${method?.toUpperCase() ?? 'GET'} ${url} ended without a response`,
      data: {
        page_unloading: pageUnloading,
        visibility: document.visibilityState,
        online: navigator.onLine,
      },
    });
  },

  /**
   * A company slug the backend does not know, which sends the visitor to /404. A deleted company's
   * slug is not reported (see CompanySlugProvider), so what is left is a link that should have resolved.
   * A log line, not an issue: it is a bad inbound link (~400 a day, mostly bots and stale links), so
   * it is searched by slug and host in Logs rather than triaged.
   */
  companySlugNotFound(slug: string | null | undefined): void {
    if (WindowResource.isNotFoundPage() || !markReportedOnce(`company_slug_not_found:${slug ?? ''}`)) {
      return;
    }

    Sentry.logger.warn('[navigation] company_slug_not_found', {
      requested_slug: slug ?? null,
      slug_source: slug ? 'url' : 'franchise_default',
      ...storefrontSnapshot(),
      ...pageSnapshot(),
    });
  },

  /**
   * The order-details page gave up and asked the customer to contact support. Raised as an issue only
   * once a customer can see that screen. While the page is unloading or hidden the customer may have
   * left before the requests finished (SHOP-WEB-4): that is logged as `load_abandoned`, and the issue
   * is raised only if the page comes back to the foreground with the failure still on screen.
   *
   * Returns a cleanup for the caller's unmount: a failure no longer on screen is not raised later.
   */
  orderLoadFailed(attrs: DiagnosticAttrs): () => void {
    const raise = () =>
      Sentry.captureMessage('[order_details] load_failed', {
        level: 'error',
        fingerprint: ['order_details', 'load_failed'],
        contexts: {
          order_load: attrs,
          storefront: storefrontSnapshot(),
          page: pageSnapshot(),
        },
      });

    if (!isPageLeaving()) {
      raise();

      return () => undefined;
    }

    Sentry.logger.info('[order_details] load_abandoned', {
      ...attrs,
      ...storefrontSnapshot(),
      ...pageSnapshot(),
    });

    const raiseOnReturn = () => {
      if (document.visibilityState !== 'visible') {
        return;
      }

      stopWaiting();
      // The listeners that clear `pageUnloading` may run after this one; the page is visible again.
      pageUnloading = false;
      raise();
    };

    const stopWaiting = () => {
      document.removeEventListener('visibilitychange', raiseOnReturn);
      window.removeEventListener('focus', raiseOnReturn);
      window.removeEventListener('pageshow', raiseOnReturn);
    };

    document.addEventListener('visibilitychange', raiseOnReturn);
    window.addEventListener('focus', raiseOnReturn);
    window.addEventListener('pageshow', raiseOnReturn);

    return stopWaiting;
  },
};

export default pageDiagnostics;
